<script setup lang="ts">
import {
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watchEffect,
} from "vue";
import OpenComboBtn from "./OpenComboBtn.vue";
import { usePopoverPosition } from "../composables/usePopoverPosition";
import useClickOutside from "../composables/useClickOutside";
import SimpleBar from "simplebar";
import MenuItem from "./MenuItem.vue";
import { locale } from "../locale";

const props = defineProps<{
  title: string;
  list: {
    deviceId: string;
    deviceName: string;
  }[];
  visibleText: string;
  noPadding?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "select", data: string): void;
}>();

const mode = inject("colormode", ref("white"));
const opened = ref(false);
const listEl = ref<HTMLElement | null>(null);
const inpEl = ref<HTMLElement | null>(null);

useClickOutside(listEl, opened);
let scroll: SimpleBar | null = null;
onMounted(() => {
  scroll = new SimpleBar(listEl.value as HTMLElement);
});
const res = () => {
  if (opened.value && listEl.value && inpEl.value) {
    usePopoverPosition(listEl.value, inpEl.value, {
      minH: 160,
      maxH: 376,
      width: "parent",
      offsetTop: 37,
      offsetBottom: 4,
      step: 36,
      offsetLeft: 0,
      autoChangePos: true,
      align: "center",
    });
    nextTick(() => {
      scroll?.recalculate();
    });
  }
};
onMounted(() => {
  window.addEventListener("resize", res);
  document.addEventListener("scroll", res);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", res);
  document.removeEventListener("scroll", res);
});

watchEffect(() => {
  if (opened.value && listEl.value && inpEl.value) {
    usePopoverPosition(listEl.value, inpEl.value, {
      minH: 160,
      maxH: 376,
      width: "parent",
      offsetTop: 37,
      offsetBottom: 4,
      step: 36,
      offsetLeft: 0,
      autoChangePos: true,
      align: "center",
    });
    nextTick(() => {
      scroll?.recalculate();
    });
  }
});
const pick = (val: string) => {
  emit("select", val);
  opened.value = false;
};
const toggle = () => {
  if (props.list.length > 0 && !props.disabled) {
    opened.value = !opened.value;
  }
};
</script>
<template>
  <div
    class="input-box"
    :class="{
      'no-padding': noPadding,
      [mode]: true,
      'empty-list': list.length === 0,
      ok: list.length > 0,
      disabled,
    }"
  >
    <div class="title">{{ title }}</div>
    <input
      ref="inpEl"
      :placeholder="locale.empty"
      readonly
      :value="visibleText"
      @click="toggle"
    />
    <OpenComboBtn class="open-ico" :class="{ opened }" @click="toggle" />
    <Teleport to="body">
      <div
        ref="listEl"
        class="list-items"
        :class="{ show: opened, [mode]: true }"
      >
        <MenuItem
          v-for="item in list"
          :id="item.deviceId"
          @click="pick(item.deviceId)"
        >
          {{ item.deviceName }}
        </MenuItem>
      </div>
    </Teleport>
  </div>
</template>
<style scoped>
.input-box {
  position: relative;
  display: flex;
  height: 76px;
  padding: 0 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
}
.input-box.no-padding {
  padding: 0;
}

.open-ico {
  color: #747474;
}

.no-padding .open-ico {
  right: 8px;
}

.opened {
  transform: rotate(180deg);
}

.white .title {
  color: #747474;
}

.black .title {
  color: #a4a4a4;
}

.title {
  font-family: Open Sans;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16.34px;
}

.white input {
  background: #fff;
  color: #292929;
  border: 1.5px solid #d8d8d8;
}

.black input {
  background: #292929;
  color: #ececec;
  border: 1.5px solid #747474;
}

input {
  height: 36px;
  width: 100%;
  padding: 8px;
  line-height: 20px;
  border: 1.5px solid #d8d8d8;
  border-radius: 4px;
  font-family: Open Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  cursor: pointer;
  padding-right: 42px;
  text-overflow: ellipsis;
}

.empty-list input {
  cursor: default;
}

input::placeholder {
  color: #a4a4a4;
}

.ok input:focus,
.ok input:hover {
  border: 1.5px solid #a4a4a4;
  outline: none;
}

.empty-list input:focus {
  outline: none;
}

.list-items {
  position: fixed;
  display: none;
  width: 100%;
  height: fit-content;
  padding: 8px 0;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  z-index: 1000;
}

.list-items.black {
  background: #292929;
}

.list-items.white {
  background-color: var(--white);
}

.list-items.show {
  display: block;
}

.disabled.white {
  .title {
    color: #a4a4a4;
  }

  input {
    color: #a4a4a4;
    cursor: default;
  }

  .open-ico {
    color: #a4a4a4;
  }

  input:focus, input:hover {
    border-color: #d8d8d8;
  }
}

.disabled.black {
  .title {
    color: #a4a4a4;
  }

  input {
    color: #a4a4a4;
    cursor: default;
  }

  .open-ico {
    color: #a4a4a4;
  }

  input:focus, input:hover {
    border-color: #747474;
  }
}
</style>
